// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// The five seconds. `resolve()` takes a position, two order sets and nothing else, and returns
// exactly what happened — a pure function, with no randomness anywhere in it.
//
// ── WHY IT IS PURE, AND WHY THAT IS THE ENTIRE NETCODE ──────────────────────────────────────────
// Both devices run this. Nothing about the board, the positions, the shots or the deaths is ever
// transmitted: a peer receives two order blobs and computes the same film. That is why host transfer
// here is a repaint rather than a takeover, why a lost message cannot hang the room (every message
// carries the whole order history, so there is no delta to lose), and why a spectator arriving on
// turn six is a few hundred bytes behind rather than unrecoverable.
//
// It is also why this file uses only the exactly-specified floating-point operations. See tuning.ts
// for the full argument; the short version is that IEEE-754 pins +, -, *, / and sqrt to the bit on
// every engine and says nothing at all about atan2, sin, cos, pow or hypot. Directions are a table
// of sixteen integer vectors; distances are compared squared; angles never exist.
//
// ── THE FOUR SYSTEMS, AND WHAT THEY MULTIPLY INTO ───────────────────────────────────────────────
// Nobody has hit points, nothing rolls a die, and a unit only ever shoots inside the wedge it is
// watching. A fight is decided by who finishes ACQUIRING first, and four things move that:
//
//   THE WEDGE    hard-edged. Outside it you do not shoot, however long you stare. So a watch is a
//                guess, written down, that you cannot take back for five seconds.
//   MOTION       walking more than halves your draw; rushing removes it entirely.
//   RANGE        each unit can be answered by exactly the thing it cannot reach.
//   THE CLOCK    aim resets the instant the line breaks, so a unit that peeks and steps back has
//                spent the WATCHER'S patience, not its own.
//
// They multiply, so the interesting situations are ones nobody enumerated. A rushing unit is unarmed
// bait that a watcher cannot punish without turning away from the lane that matters. Two stalking
// units on one line simply trade, so the only thing that decides a straight fight is who was already
// stationary when the corner opened — which turns the second-marks on the path ribbon into a weapon.
// A mast frozen by a body thrown into it is a calculable, frequently correct sacrifice that no rule
// mentions.
//
// ── DEATHS ARE SIMULTANEOUS, ON PURPOSE ─────────────────────────────────────────────────────────
// Every unit is evaluated against the state at the START of a tick and deaths are applied at the
// END. Two units that finish aiming in the same twentieth of a second both fire and both die. Take
// the trade away and the first shot of a match decides it — and a game the loser never gets to shoot
// back in is a game whose opening is its ending.

import {
  AIM_FULL,
  SECTORS,
  STANCES,
  UNITS,
  WINDOW_TICKS,
  type UnitSpec,
} from './tuning';
import {
  N,
  blocksFeet,
  canSeeTile,
  idx,
  inAnyMastField,
  inMastField,
  makeMap,
  mastsFor,
  mastField,
  tx,
  ty,
  type GameMap,
} from './map';
import { RUSH, decode, encode, sanitizeSide, type SideOrders } from './orders';
import { modeOf, type Mode, type ModeId } from './modes';

export type Side = 0 | 1;

/**
 * The sixteen watch directions, as integer vectors scaled by 1024, written out by hand.
 *
 * Hand-written rather than generated from Math.cos for exactly one reason: a table built at module
 * load from a transcendental is a table that can differ in its last bit between two engines, and
 * every wedge test in the game reads it. `tests/rules.test.ts` proves it is a real circle — sixteen
 * evenly spaced vectors of equal length — so "hand-written" cannot quietly become "wrong".
 */
export const DIRS: ReadonlyArray<readonly [number, number]> = [
  [1024, 0],
  [946, 392],
  [724, 724],
  [392, 946],
  [0, 1024],
  [-392, 946],
  [-724, 724],
  [-946, 392],
  [-1024, 0],
  [-946, -392],
  [-724, -724],
  [-392, -946],
  [0, -1024],
  [392, -946],
  [724, -724],
  [946, -392],
];

/** The nearest of the sixteen directions to a vector — argmax of the dot product, ties to the lowest. */
export function sectorOf(dx: number, dy: number): number {
  let best = 0;
  let bestDot = -Infinity;
  for (let s = 0; s < SECTORS; s++) {
    const d = dx * DIRS[s][0] + dy * DIRS[s][1];
    if (d > bestDot) {
      bestDot = d;
      best = s;
    }
  }
  return best;
}

/** How many sectors apart two directions are, the short way round. */
export const sectorGap = (a: number, b: number): number => {
  const d = Math.abs(a - b) % SECTORS;
  return d > SECTORS / 2 ? SECTORS - d : d;
};

/**
 * Is the vector (dx,dy) inside the wedge of half-width `wedge` sectors around sector `s`?
 *
 * The test lives in SECTOR SPACE — quantise the bearing, then compare sector indices — and not in
 * angle space, for two reasons that both turned out to matter.
 *
 * The first is symmetry. Comparing a dot product against cos(half-angle) sounds exact and is not:
 * the sixteen direction vectors are integers, so their lengths differ in the fourth decimal, and a
 * target sitting exactly on the edge of a wedge is admitted on one side and refused on the other.
 * That is a unit whose watch covers very slightly more ground to its left than its right — which no
 * screenshot would ever show and which the first symmetry test written found immediately.
 *
 * The second is that the player is looking at sixteen sectors. The arrowhead snaps to them, the
 * threat overlay shades them, the wire carries one. Making the RULE agree with the picture means
 * what you see is exactly what is covered, with no boundary tiles that look shaded and are not.
 *
 * A wedge of k therefore covers 2k+1 sectors: half-widths of 33.75, 56.25 and 78.75 degrees.
 */
export function inWedge(dx: number, dy: number, s: number, wedge: number): boolean {
  if (dx === 0 && dy === 0) return true;
  return sectorGap(sectorOf(dx, dy), s) <= wedge;
}

export interface UnitState {
  side: Side;
  /** Index within its own side, which is also its type index. */
  n: number;
  spec: UnitSpec;
  /** Tile-space position. Whole numbers are tile corners; a unit stands on a centre. */
  x: number;
  y: number;
  /** Which of the sixteen directions it is watching. */
  face: number;
  alive: boolean;
}

export interface MatchState {
  seed: number;
  modeId: ModeId;
  mode: Mode;
  map: GameMap;
  /** Turns already resolved. The turn being planned is `turn`. */
  turn: number;
  units: UnitState[];
  /** Ticks of uncontested holding banked by each side. */
  hold: [number, number];
  over: boolean;
  /** -1 while live or drawn; otherwise the winning side. */
  winner: number;
  ending: 'live' | 'hold' | 'wipe' | 'time' | 'draw';
  /** Every turn's sanitised orders, both sides, as flat ints — the whole match. */
  log: Array<[number[], number[]]>;
}

export type EventKind = 'fire' | 'kill' | 'hold';

export interface SimEvent {
  kind: EventKind;
  tick: number;
  /** The actor, as an index into `units`. */
  u: number;
  /** Who it happened to, or the side holding the mast, or -1. */
  v: number;
}

export interface Replay {
  ticks: number;
  n: number;
  /** ticks x n x 2, flat. */
  pos: Float32Array;
  face: Uint8Array;
  alive: Uint8Array;
  /** Aim points at the end of each tick — what the audit reads to prove nobody failed to fire. */
  aim: Int32Array;
  /** Who each unit was aiming at, or -1. */
  target: Int8Array;
  /** 2 x ticks x n — whether side s can see unit u at that tick. Ambush's lens, and only a lens. */
  vis: Uint8Array;
  hold: Int32Array;
  masts: number[];
  events: SimEvent[];
}

// ── setup ───────────────────────────────────────────────────────────────────────────────────────

export function createMatch(seed: number, modeId: string): MatchState {
  const mode = modeOf(modeId);
  const map = makeMap(seed, mode);
  const units: UnitState[] = [];
  for (const side of [0, 1] as Side[]) {
    map.spawns[side].forEach((tile, n) => {
      units.push({
        side,
        n,
        spec: UNITS[n % UNITS.length],
        x: tx(tile) + 0.5,
        y: ty(tile) + 0.5,
        // Looking at the other end of the board is the only sensible thing to start watching.
        face: side === 0 ? 12 : 4,
        alive: true,
      });
    });
  }
  return {
    seed,
    modeId: mode.id,
    mode,
    map,
    turn: 0,
    units,
    hold: [0, 0],
    over: false,
    winner: -1,
    ending: 'live',
    log: [],
  };
}

export const startsOf = (s: MatchState, side: Side): number[] =>
  s.units.filter((u) => u.side === side).map((u) => idx(Math.floor(u.x), Math.floor(u.y)));

export const unitsOf = (s: MatchState, side: Side): UnitState[] => s.units.filter((u) => u.side === side);
export const aliveCount = (s: MatchState, side: Side): number => unitsOf(s, side).filter((u) => u.alive).length;

export function cloneState(s: MatchState): MatchState {
  return {
    ...s,
    units: s.units.map((u) => ({ ...u })),
    hold: [s.hold[0], s.hold[1]],
    log: s.log.map((e) => [e[0].slice(), e[1].slice()] as [number[], number[]]),
  };
}

// ── the resolver ────────────────────────────────────────────────────────────────────────────────

const EMPTY = {
  pos: new Float32Array(0),
  face: new Uint8Array(0),
  alive: new Uint8Array(0),
  aim: new Int32Array(0),
  target: new Int8Array(0),
  vis: new Uint8Array(0),
  hold: new Int32Array(0),
};

/**
 * Run one five-second window.
 *
 * `record` is what the bot turns off: it resolves hundreds of candidate futures per turn and needs
 * the outcome, not the film. With it off this allocates four small scratch arrays and nothing else.
 */
export function resolve(s: MatchState, oa: SideOrders, ob: SideOrders, record = true): Replay {
  const map = s.map;
  const units = s.units;
  const n = units.length;
  const ticks = WINDOW_TICKS;
  const masts = mastsFor(s.mode, s.turn);
  const events: SimEvent[] = [];

  const aim = new Int32Array(n);
  const target = new Int32Array(n).fill(-1);
  const leg = new Int32Array(n);
  const moving = new Uint8Array(n);
  const dying = new Uint8Array(n);
  const revealed = new Uint8Array(n);

  const orderFor = (u: number): SideOrders[number] => (units[u].side === 0 ? oa : ob)[units[u].n];

  const rep: Replay = record
    ? {
        ticks,
        n,
        pos: new Float32Array(ticks * n * 2),
        face: new Uint8Array(ticks * n),
        alive: new Uint8Array(ticks * n),
        aim: new Int32Array(ticks * n),
        target: new Int8Array(ticks * n).fill(-1),
        vis: new Uint8Array(2 * ticks * n),
        hold: new Int32Array(ticks * 2),
        masts,
        events,
      }
    : { ticks, n, ...EMPTY, masts, events };

  for (let t = 0; t < ticks; t++) {
    // ── 1. movement ────────────────────────────────────────────────────────────────────────────
    for (let u = 0; u < n; u++) {
      const unit = units[u];
      if (!unit.alive) continue;
      const o = orderFor(u);
      const stance = STANCES[o.stance];
      let budget = (unit.spec.speed * stance.speed) / 100 / 20;
      let moved = false;
      while (budget > 1e-9 && leg[u] < o.path.length) {
        const wp = o.path[leg[u]];
        const gx = tx(wp) + 0.5;
        const gy = ty(wp) + 0.5;
        const dx = gx - unit.x;
        const dy = gy - unit.y;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d <= budget) {
          unit.x = gx;
          unit.y = gy;
          budget -= d;
          leg[u]++;
          if (d > 1e-9) moved = true;
        } else {
          unit.x += (dx / d) * budget;
          unit.y += (dy / d) * budget;
          if (o.watch < 0) unit.face = sectorOf(dx, dy);
          budget = 0;
          moved = true;
        }
      }
      moving[u] = moved ? 1 : 0;
      if (o.watch >= 0) unit.face = o.watch;
    }

    // ── 2. the mast ────────────────────────────────────────────────────────────────────────────
    let holders = 0;
    for (let u = 0; u < n; u++) {
      const unit = units[u];
      if (unit.alive && inAnyMastField(masts, unit.x, unit.y)) holders |= 1 << unit.side;
    }
    if (holders === 1 || holders === 2) {
      const side = holders === 1 ? 0 : 1;
      s.hold[side]++;
      if (s.hold[side] >= s.mode.holdTarget && !s.over) {
        events.push({ kind: 'hold', tick: t, u: -1, v: side });
        s.over = true;
        s.winner = side;
        s.ending = 'hold';
      }
    }

    // ── 3. sight, cone, aim ────────────────────────────────────────────────────────────────────
    dying.fill(0);
    for (let u = 0; u < n; u++) {
      const shooter = units[u];
      if (!shooter.alive) {
        aim[u] = 0;
        target[u] = -1;
        continue;
      }
      const o = orderFor(u);
      const range2 = shooter.spec.range * shooter.spec.range;
      const st = idx(Math.floor(shooter.x), Math.floor(shooter.y));

      // Keep the target you already hold if the line still stands — hysteresis, so a unit does not
      // flick between two enemies and finish aiming at neither.
      let best = -1;
      let bestD2 = Infinity;
      const held = target[u];
      if (held >= 0 && units[held].alive && shootable(map, shooter, units[held], st, range2)) {
        best = held;
      } else {
        for (let v = 0; v < n; v++) {
          const foe = units[v];
          if (!foe.alive || foe.side === shooter.side) continue;
          const dx = foe.x - shooter.x;
          const dy = foe.y - shooter.y;
          const d2 = dx * dx + dy * dy;
          if (d2 > range2 || d2 >= bestD2) continue;
          if (!shootable(map, shooter, foe, st, range2)) continue;
          best = v;
          bestD2 = d2;
        }
      }

      if (best < 0) {
        // The line broke. Aim is spent, not stored — which is what makes a peek cost the person
        // peeking rather than the person watching.
        aim[u] = 0;
        target[u] = -1;
        continue;
      }
      if (target[u] !== best) aim[u] = 0;
      target[u] = best;

      // Rush is unarmed whether it is moving or not. Standing still normally restores the full draw
      // — that is the point of standing still — but a rushing unit has no draw to restore, which is
      // what makes throwing a body into the mast to freeze it a real play rather than a mistake.
      const stanceAim = STANCES[o.stance].aim;
      const rate = stanceAim === 0 ? 0 : moving[u] ? (AIM_FULL * stanceAim) / 100 : AIM_FULL;
      if (rate <= 0) {
        aim[u] = 0;
        continue;
      }
      aim[u] += rate;
      if (aim[u] >= shooter.spec.acquire * AIM_FULL) {
        aim[u] = 0;
        revealed[u] = 1;
        events.push({ kind: 'fire', tick: t, u, v: best });
        dying[best] = 1;
      }
    }

    // ── 4. deaths, all at once ─────────────────────────────────────────────────────────────────
    for (let v = 0; v < n; v++) {
      if (!dying[v] || !units[v].alive) continue;
      units[v].alive = false;
      events.push({ kind: 'kill', tick: t, u: v, v: -1 });
      aim[v] = 0;
      target[v] = -1;
      for (let u = 0; u < n; u++) if (target[u] === v) target[u] = -1;
    }

    // ── 5. the film ────────────────────────────────────────────────────────────────────────────
    if (record) {
      for (let u = 0; u < n; u++) {
        const unit = units[u];
        rep.pos[(t * n + u) * 2] = unit.x;
        rep.pos[(t * n + u) * 2 + 1] = unit.y;
        rep.face[t * n + u] = unit.face;
        rep.alive[t * n + u] = unit.alive ? 1 : 0;
        rep.aim[t * n + u] = aim[u];
        rep.target[t * n + u] = target[u];
      }
      rep.hold[t * 2] = s.hold[0];
      rep.hold[t * 2 + 1] = s.hold[1];
      for (const side of [0, 1] as Side[]) {
        for (let u = 0; u < n; u++) {
          const unit = units[u];
          let seen = unit.side === side ? 1 : 0;
          // A unit that has fired is given away for the rest of the window: the one extra rule
          // Ambush adds, and the reason a shot in the dark is a decision rather than a freebie.
          if (!seen && unit.alive && revealed[u]) seen = 1;
          if (!seen && unit.alive) {
            const ut = idx(Math.floor(unit.x), Math.floor(unit.y));
            for (let w = 0; w < n; w++) {
              const eye = units[w];
              if (eye.side !== side || !eye.alive) continue;
              const dx = unit.x - eye.x;
              const dy = unit.y - eye.y;
              if (dx * dx + dy * dy > eye.spec.range * eye.spec.range) continue;
              if (!canSeeTile(map, idx(Math.floor(eye.x), Math.floor(eye.y)), ut)) continue;
              seen = 1;
              break;
            }
          }
          rep.vis[(side * ticks + t) * n + u] = seen;
        }
      }
    }
    if (s.over) return record ? { ...rep, ticks: t + 1 } : rep;
  }
  return rep;
}

/** Range, then the wall, then the wedge — two arithmetic ops, then a bit lookup, then the quantiser. */
function shootable(map: GameMap, a: UnitState, b: UnitState, aTile: number, range2: number): boolean {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (dx * dx + dy * dy > range2) return false;
  if (!canSeeTile(map, aTile, idx(Math.floor(b.x), Math.floor(b.y)))) return false;
  return inWedge(dx, dy, a.face, a.spec.wedge);
}

/** Can this unit see that one, ignoring wedges? Sight is all round; only shooting is a wedge. */
export function spots(map: GameMap, eye: UnitState, foe: UnitState): boolean {
  const dx = foe.x - eye.x;
  const dy = foe.y - eye.y;
  if (dx * dx + dy * dy > eye.spec.range * eye.spec.range) return false;
  return canSeeTile(map, idx(Math.floor(eye.x), Math.floor(eye.y)), idx(Math.floor(foe.x), Math.floor(foe.y)));
}

/**
 * Commit a turn: sanitise both sides against the board, resolve, score, append to the log.
 *
 * The log holds the SANITISED orders, so replaying it reproduces the match byte for byte even when
 * what arrived over the wire was nonsense.
 */
export function commitTurn(s: MatchState, rawA: SideOrders, rawB: SideOrders, record = true): Replay {
  const oa = sanitizeSide(s.map, startsOf(s, 0), rawA, s.mode.units);
  const ob = sanitizeSide(s.map, startsOf(s, 1), rawB, s.mode.units);
  const rep = resolve(s, oa, ob, record);
  s.log.push([encode(oa), encode(ob)]);
  s.turn++;
  score(s);
  return rep;
}

function score(s: MatchState): void {
  if (s.over) return;
  const a = aliveCount(s, 0);
  const b = aliveCount(s, 1);
  if (a === 0 || b === 0) {
    s.over = true;
    if (a !== b) {
      s.winner = a > b ? 0 : 1;
      s.ending = 'wipe';
      return;
    }
    // EVERYBODY DIED — and this is the case that decides whether the mast is a mechanic or a
    // decoration. Trades are common here on purpose (two units watching the same line both fire on
    // the same tick), so mutual annihilation is not an edge case: measured, it was more than a third
    // of all matches, and calling every one of them a draw made the objective irrelevant AND made
    // the most common ending the least satisfying one. So if nobody is left standing, the side that
    // banked more time on the mast takes it. The mast is what the match was about.
    const byHold = s.hold[0] - s.hold[1];
    s.winner = byHold === 0 ? -1 : byHold > 0 ? 0 : 1;
    s.ending = byHold === 0 ? 'draw' : 'hold';
    return;
  }
  if (s.turn >= s.mode.turns) {
    s.over = true;
    // A total order, so the clock decides rather than shrugging: banked ticks first, then bodies.
    const byHold = s.hold[0] - s.hold[1];
    const byUnits = a - b;
    const v = byHold !== 0 ? byHold : byUnits;
    s.winner = v === 0 ? -1 : v > 0 ? 0 : 1;
    s.ending = v === 0 ? 'draw' : 'time';
  }
}

/**
 * Rebuild a whole match from (seed, mode, log) — what a peer does when it receives an order history
 * longer than its own, including a spectator that arrived on turn six. It is the reason nothing else
 * ever has to be transmitted or reconciled.
 */
export function derive(seed: number, modeId: string, log: Array<[number[], number[]]>): MatchState {
  const s = createMatch(seed, modeId);
  for (const [a, b] of log) {
    if (s.over) break;
    commitTurn(s, decode(a, s.mode.units), decode(b, s.mode.units), false);
  }
  return s;
}

/** FNV-1a over everything two peers must agree about. A mismatch is a desync, and is reported as one. */
export function digest(s: MatchState): number {
  let h = 0x811c9dc5;
  const mix = (v: number): void => {
    h ^= Math.round(v * 4096) | 0;
    h = Math.imul(h, 0x01000193);
  };
  mix(s.turn);
  mix(s.winner);
  mix(s.over ? 1 : 0);
  mix(s.hold[0]);
  mix(s.hold[1]);
  for (const u of s.units) {
    mix(u.x);
    mix(u.y);
    mix(u.face);
    mix(u.alive ? 1 : 0);
  }
  return h >>> 0;
}

/** Legal destinations for a unit this turn: everything it could actually stand on. */
export function walkable(map: GameMap): boolean[] {
  const out = new Array<boolean>(N);
  for (let i = 0; i < N; i++) out[i] = !blocksFeet(map, tx(i), ty(i));
  return out;
}

export { RUSH, mastsFor, mastField, inMastField, inAnyMastField };
